import React, { ReactNode, useMemo } from "react";
import * as R from "ramda";
import { diff } from "deep-diff";
import { css } from "glamor";
import { Divider } from "antd";

import {
    fmtTitles,
    getTitleFromPath,
    getJsonSchemaFromPath,
} from "@arachnys/flow-app/src/util";
import {
    filterEmptyValues,
    filterHiddenValues,
    formatWithJsonSchema,
} from "@arachnys/flow-app/src/formatters";
import GenericViewer from "@arachnys/flow-app/src/component/GenericViewer";
import { NARRATIVE_EVENT_TYPES } from "@arachnys/flow-app/src/config";
import { Entity, Relation } from "@arachnys/flow-app/src/types";

const styles = {
    updateRow: css({
        "&:nth-child(n+2)": {
            marginTop: 10,
        },
    }),
    listContainer: css({
        padding: "0 0 10px 10px",
    }),
    processSummary: css({
        whiteSpace: "normal",
    }),
};

const diffRelations = (
    oldRelations: Array<Relation>,
    newRelations: Array<Relation>
) => {
    // Determine added/removed relations
    // We don't have any ulids, so the name will have to do
    // Also, we don't include updates to existing relations
    const compare = (a: Relation, b: Relation) =>
        a.fromEntity.name === b.fromEntity.name;
    return {
        removed: R.differenceWith(compare, oldRelations, newRelations),
        added: R.differenceWith(compare, newRelations, oldRelations),
    };
};

const formatEntityFromRelation = ({
    relation,
    schema,
}: {
    relation: Relation;
    schema: any;
}): Entity => {
    let entity = relation.fromEntity;
    entity = filterEmptyValues({
        jsonSchema: schema,
        data: entity,
    });
    entity = formatWithJsonSchema({
        jsonSchema: schema,
        data: entity,
    });
    entity = filterHiddenValues({
        jsonSchema: schema,
        data: entity,
    });
    return entity;
};

const RelationsBlock = ({
    items,
    label,
    schema,
}: {
    schema: any;
    label: string;
    items: Array<Relation>;
}) => {
    const formatted = useMemo(
        () =>
            items.map(relation =>
                formatEntityFromRelation({ relation, schema })
            ),
        [items, schema]
    );

    // useMemo must be called every time,
    // i.e. this cannot be moved up.
    if (!items.length) {
        return null;
    }

    return (
        <div>
            <b>{label}:</b>
            {formatted.map((entity, index) => {
                return (
                    <React.Fragment key={entity.name}>
                        {index > 0 ? <Divider /> : null}
                        <GenericViewer data={entity} />
                    </React.Fragment>
                );
            })}
        </div>
    );
};

const RelationsUpdatedSummary = ({
    record,
    schema,
}: {
    record: any;
    schema: any;
}) => {
    const { added, removed } = diffRelations(
        record.detail.relationsBefore.relations,
        record.detail.relationsMerged.relations
    );
    return (
        <>
            <RelationsBlock items={added} schema={schema} label="Added" />
            <RelationsBlock items={removed} schema={schema} label="Removed" />
        </>
    );
};

const resultsCount = (results: number): string =>
    `${results} result${results === 1 ? "" : "s"} `;
const generateResults = (path: Array<string>, record: any): string =>
    resultsCount(R.pathOr(0, path, record));

const renderSummaryRows = (obj: any) =>
    Object.entries(obj).map(([key, val]) => {
        if (!val) return;
        switch (val.constructor) {
            case Object:
                return (
                    <div {...styles.updateRow}>
                        <div>
                            {Object.entries(val).map(([nKey, nVal]) =>
                                renderSummaryRows({ [nKey]: nVal })
                            )}
                        </div>
                    </div>
                );
            case Array:
                return (
                    <div {...styles.updateRow}>
                        <b>{key}</b>
                        {renderSummaryRows({ ...val })}
                    </div>
                );
            default:
                return (
                    val && (
                        <div {...styles.updateRow}>
                            {/* Hide array indexes when called from Array clause */}
                            {!/^\d+$/.test(key) && <b>{key}</b>}
                            <p>{val}</p>
                        </div>
                    )
                );
        }
    });

const getDeepTitle = (path: Array<string | number>, schema: any): string =>
    path
        .map((component: any, i: number) =>
            !Number.isInteger(component)
                ? getTitleFromPath(path.slice(0, i + 1).join("."), schema)
                : null
        )
        .join(" ");

const getPathHidden = (path: Array<string | number>, schema: any): boolean =>
    path.some(
        (component: any, i: number) =>
            !Number.isInteger(component) &&
            getJsonSchemaFromPath(path.slice(0, i + 1).join("."), schema)
                .isHidden
    );

export const NarrativeSummary = ({
    record,
    schema,
}: {
    record: any;
    schema: any;
}) => {
    let data: ReactNode = "";
    let data_diff: any;
    let results: number;
    let showAccgValues: Boolean;
    const resultsList = {
        changed: "",
        yes: "",
        no: "",
        maybe: "",
        flag: "",
        accept: "",
        applicable: "",
        notApplicable: "",
        infoRequired: "",
    };

    switch (R.prop("action", record)) {
        case NARRATIVE_EVENT_TYPES.COMMENT_POSTED:
            data = R.path(["detail", "content"], record);
            break;
        case NARRATIVE_EVENT_TYPES.RESULT_SELECTED:
            data = R.path(["detail", "searchResult", "entity", "name"], record);
            break;
        case NARRATIVE_EVENT_TYPES.ENTITY_UPDATED:
            data_diff = diff(
                R.path(["detail", "entityBefore"], record),
                R.path(["detail", "entityMerged"], record)
            );
            if (data_diff) {
                data = data_diff.map((e: any) => {
                    if (getPathHidden(e.path, schema)) {
                        return undefined;
                    }

                    if (e.kind === "E" || e.kind === "N") {
                        if (!e.rhs) return;
                        // new or edited property
                        if (e.rhs.constructor === Object) {
                            return renderSummaryRows(
                                fmtTitles({ [e.path[0]]: e.rhs }, schema)
                            );
                        } else {
                            const label = getDeepTitle(e.path, schema);
                            return renderSummaryRows({
                                [label]: e.rhs,
                            });
                        }
                    } else if (e.kind === "A") {
                        if (!e.item.rhs) return;
                        // array property modified
                        if (e.item.rhs.constructor === Object) {
                            return renderSummaryRows(
                                fmtTitles({ [e.path[0]]: e.item.rhs }, schema)
                            );
                        } else {
                            const label = getDeepTitle(e.path, schema);
                            return renderSummaryRows({
                                [`${label} #${e.index + 1}`]: e.item.rhs,
                            });
                        }
                    }
                });
            } else {
                data = "Entity unmodified";
            }
            break;
        case NARRATIVE_EVENT_TYPES.DOCUMENT_UPLOADED: {
            const { detail } = record;
            data = (
                <>
                    <b>Evidence captured</b>
                    <div>
                        <a
                            rel="noopener noreferrer"
                            target="_blank"
                            href={detail.url}
                        >
                            {detail.title}
                        </a>
                    </div>
                </>
            );
            break;
        }
        case NARRATIVE_EVENT_TYPES.RELATIONS_UPDATED:
            data = <RelationsUpdatedSummary record={record} schema={schema} />;
            break;
        case NARRATIVE_EVENT_TYPES.USER_ASSIGNED:
            data = (
                <div className="updateRow" key="username">
                    <b>Assigned to</b>
                    <br />
                    {R.path(["detail", "assignedTo", "userName"], record)}
                </div>
            );
            break;
        case NARRATIVE_EVENT_TYPES.USER_UNASSIGNED:
            data = "";
            break;
        case NARRATIVE_EVENT_TYPES.TASK_DISPOSITIONED:
            resultsList.changed = generateResults(
                ["detail", "changed"],
                record
            );

            results = R.pathOr(0, ["detail", "results"], record);
            showAccgValues =
                results.hasOwnProperty("Applicable") ||
                results.hasOwnProperty("Not Applicable") ||
                results.hasOwnProperty("Additional Info Required");

            resultsList.yes = generateResults(["YES"], results);
            resultsList.no = generateResults(["NO"], results);
            resultsList.maybe = generateResults(["MAYBE"], results);
            resultsList.applicable = generateResults(["Applicable"], results);
            resultsList.notApplicable = generateResults(
                ["Not Applicable"],
                results
            );
            resultsList.infoRequired = generateResults(
                ["Additional Info Required"],
                results
            );

            data = (
                <div className={`${styles.processSummary}`}>
                    {resultsList.changed}
                    changed
                    <br />
                    {showAccgValues ? (
                        <>
                            {resultsList.applicable}
                            marked as &quot;Applicable&quot;
                            <br />
                            {resultsList.notApplicable} marked as &quot;Not
                            Applicable&quot;
                            <br />
                            {resultsList.infoRequired} marked as
                            &quot;Additional Info Required&quot;
                        </>
                    ) : (
                        <>
                            {resultsList.yes}
                            marked as &quot;YES&quot;
                            <br />
                            {resultsList.no} marked as &quot;NO&quot;
                            <br />
                            {resultsList.maybe} marked as &quot;MAYBE&quot;
                        </>
                    )}
                </div>
            );
            break;

        case NARRATIVE_EVENT_TYPES.TASK_REVIEWED:
            resultsList.changed = generateResults(
                ["detail", "changed"],
                record
            );
            resultsList.flag = generateResults(
                ["detail", "results", "Flag"],
                record
            );
            resultsList.accept = generateResults(
                ["detail", "results", "Accept"],
                record
            );

            data = (
                <div>
                    {resultsList.changed}
                    changed
                    <br />
                    {resultsList.flag}
                    flagged
                    <br />
                    {resultsList.accept}
                    accepted
                </div>
            );
            break;
        case NARRATIVE_EVENT_TYPES.TASK_PROCESSED_SUMMARY:
            resultsList.changed = resultsCount(
                R.pathOr(0, ["detail", "changed"], record)
            );

            data = (
                <div className={`${styles.processSummary}`}>
                    {resultsList.changed}
                    changed
                    <br />
                    {R.pathOr([], ["detail", "sections"], record).map(
                        (section: {
                            title: string;
                            results: Array<string>;
                        }) => (
                            <div key={section.title}>
                                <strong>{section.title}:</strong>
                                <div className={`${styles.listContainer}`}>
                                    {section.results.map(result => (
                                        <div key={result}>{result}</div>
                                    ))}
                                </div>
                            </div>
                        )
                    )}
                </div>
            );
            break;
        default:
            data = "Not found";
    }
    return data;
};
